import {
  Drawer,
  List,
  IconButton,
  Typography,
  Box,
  Divider,
  Icon,
  Grid,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import LogoutButton from "../components/logoutButton";
import ThemeToggleButton from "../components/themeButton";
import LanguageButton from "../components/langButton";
import DateTimeDisplay from "./dateTimeDisplay";
import SyncButton from "./SyncButton";
import NavCloseDrawer from "./navCloseDrawer";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useServerStatus from "../hooks/useServerStatus";
import useActiveTimeSlot from "../hooks/useActiveTimeSlot";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import { usePermissions } from '../contexts/PermissionsContext';

import ServerOn from "../images/Icon Left (Server ON).svg";
import ServerOff from "../images/Icon Left (Server OFF).svg";
import meetingDashboardOrangeIcon from "../assets/icons/meeting-dashboard-orange.png";
import spaLogoOrange from "../assets/icons/spa-logo-orange.png";
import centroPagheLogo from "../assets/branding/centro-paghe.png";
import gruppoSigesLogo from "../assets/branding/gruppo-siges.png";
import NavigatorLogo from "../images/Vector (Navigator bar).svg";

const menuGroupPalette = {
  operativita: {
    labelKey: "menuGroups.operativity",
    color: "#0B6E99",
    background: "#E8F6FB",
  },
  prenotazioni: {
    labelKey: "menuGroups.reservationsCrm",
    color: "#D47200",
    background: "#FFF4E4",
  },
  catalogo: {
    labelKey: "menuGroups.catalogMenu",
    color: "#28745C",
    background: "#EAF7F1",
  },
  amministrazione: {
    labelKey: "menuGroups.administration",
    color: "#6B4CA5",
    background: "#F2ECFF",
  },
  sistema: {
    labelKey: "menuGroups.system",
    color: "#455A64",
    background: "#EEF3F5",
  },
};

function AppSidebar() {
  const { can, canVisit } = usePermissions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sfondo, setSfondo] = useState("");
  const [bordo, setBordo] = useState("");
  const [icona, setIcona] = useState("");
  const canSeeLogs = can('logs:view');
  const chiudiMenu = () => setOpen(false);
  const isServerOnline = useServerStatus(`/api/status/ping`);
  const activeTimeSlot = useActiveTimeSlot();

  const theme = useTheme();

  const tenantType = localStorage.getItem("tenantType");
  const normalizedTenantType = String(tenantType || "").toUpperCase();
  const isConferenceTenant = normalizedTenantType === "SALE_CONGRESSI";
  const isSpaTenant = normalizedTenantType === "SPA";
  const dashboardLogo = isSpaTenant
    ? spaLogoOrange
    : isConferenceTenant
      ? meetingDashboardOrangeIcon
      : NavigatorLogo;
  const goToPrimaryPlanner = () => {
    if (isConferenceTenant) navigate("/prenotazione");
    if (isSpaTenant) navigate("/spa/planner");
  };

  const renderMenuGroup = ({ key, items }) => {
    items = items.filter((item) => canVisit(item.to));
    const group = menuGroupPalette[key];
    if (!group || !items.length) return null;

    return (
      <Box
        key={key}
        sx={{
          mx: 1.5,
          mb: 1.25,
          py: 1,
          borderRadius: 3,
          border: `1px solid ${alpha(group.color, 0.2)}`,
          borderLeft: `6px solid ${group.color}`,
          background:
            theme.palette.mode === "dark"
              ? alpha(group.color, 0.12)
              : `linear-gradient(135deg, ${group.background} 0%, rgba(255,255,255,0.95) 100%)`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "none"
              : "0 8px 22px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            px: 1.5,
            pb: 0.5,
            color: group.color,
            fontWeight: 950,
            letterSpacing: 0.8,
            textTransform: "uppercase",
          }}
        >
          {t(group.labelKey)}
        </Typography>
        {items.map((item) => (
          <NavCloseDrawer
            key={item.to}
            to={item.to}
            primary={item.primary}
            chiudiMenu={chiudiMenu}
            accent={group.color}
          />
        ))}
      </Box>
    );
  };

  const apriMenu = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    )
      return;
    setOpen(open);
    navigate("#drawer", { replace: true });
  };

  useEffect(() => {
    setOpen(false);
    if (isServerOnline) {
      setSfondo("#D8FFE5");
      setBordo("#125605");
      setIcona(ServerOn);
    } else {
      setSfondo("#FFDEDE");
      setBordo("#E00C0C");
      setIcona(ServerOff);
    }
    const handlePopState = () => {
      setOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isServerOnline]);

  return (
    <>
      <Box
        display={"flex"}
        alignItems={"center"}
        width={"100%"}
        sx={{
          height: { xs: "48px", sm: "56px" },
          background: theme.palette.background.default,
        }}
      >
        <Grid
          container
          display={"flex"}
          justifyContent={"space-between"}
          alignContent={"center"}
          position={"fixed"}
          zIndex={theme.zIndex.appBar}
          height={{ xs: "48px", sm: "58px" }}
          sx={{
            background: theme.palette.background.default,
            paddingLeft: { xs: 1, sm: "1.719vw" },
            paddingRight: { xs: 1, sm: "1.719vw" },
            paddingTop: 0,
            paddingBottom: 0,
            flexWrap: "nowrap",
          }}
          alignItems={"center"}
        >
          <Box
            aria-label="Gruppo Centro Paghe e Gruppo Siges"
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "none",
              "@media (min-width: 768px)": { display: "flex" },
              alignItems: "center",
              justifyContent: "center",
              gap: "clamp(24px, 2.5vw, 48px)",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <Box
              component="img"
              src={centroPagheLogo}
              alt="Gruppo Centro Paghe"
              sx={{
                width: "auto",
                height: "clamp(32px, 2.2vw, 40px)",
                maxWidth: 152,
                objectFit: "contain",
              }}
            />
            <Box
              component="img"
              src={gruppoSigesLogo}
              alt="Gruppo Siges"
              sx={{
                width: "auto",
                height: "clamp(32px, 2.2vw, 40px)",
                maxWidth: 140,
                objectFit: "contain",
              }}
            />
          </Box>

          <Grid
            item
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            onClick={goToPrimaryPlanner}
            sx={{
              width: { xs: "auto", sm: "auto" },
              flex: { xs: "1 1 auto", sm: "0 1 auto" },
              minWidth: 0,
              overflow: "hidden",
              cursor: isConferenceTenant || isSpaTenant ? "pointer" : "default",
            }}
          >
            <Box sx={{ pr: { xs: 0.75, sm: "1.146vw" }, flexShrink: 0 }}>
              <img
                src={dashboardLogo}
                alt="Logo"
                style={{ width: "28px", height: "28px", objectFit: "contain", display: "block" }}
              />
            </Box>

            <Typography
              fontSize={{ xs: "14px", sm: "16px" }}
              lineHeight={{ xs: "20px", sm: "175%" }}
              fontWeight={"bold"}
              sx={{ flexShrink: 0 }}
            >
              {t("dashboard")}
            </Typography>

            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <DateTimeDisplay />
            </Box>

            <Box
              sx={{
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.25, sm: 0.5 },
                ml: { xs: 0.75, sm: 1.5 },
                borderRadius: "999px",
                background: activeTimeSlot && activeTimeSlot !== "NO SLOT"
                  ? "#f59e0b"
                  : "transparent",
                color: activeTimeSlot && activeTimeSlot !== "NO SLOT"
                  ? "#111"
                  : "transparent",
                fontWeight: 700,
                fontSize: { xs: "11px", sm: "12px" },
                display: {
                  xs: "none",
                  sm: activeTimeSlot && activeTimeSlot !== "NO SLOT" ? "inline-flex" : "none",
                },
                alignItems: "center",
                minHeight: { xs: "24px", sm: "28px" },
                flexShrink: 0,
              }}
            >
              {activeTimeSlot}
            </Box>
          </Grid>

          <Grid
            item
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            sx={{
              width: { xs: "auto", sm: "auto" },
              flex: { xs: "0 0 auto", sm: "0 0 auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              gap: { xs: 0.25, sm: 0 },
              minWidth: 0,
              paddingRight: { xs: 0, sm: "2%" },
            }}
          >
            <Typography
              color={"black"}
              display={"flex"}
              alignItems={"center"}
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                border: `1px solid ${bordo}`,
                borderRadius: "8px",
                background: sfondo,
                paddingLeft: { xs: "8px", sm: "16px" },
                paddingRight: { xs: "8px", sm: "16px" },
                minHeight: { xs: 26, sm: "auto" },
                flexShrink: 0,
              }}
            >
              {t("server")}
              <Icon sx={{ paddingLeft: "8px" }}>
                <img src={icona} alt="Server status" style={{ marginBottom: "6px" }} />
              </Icon>
            </Typography>

            <ThemeToggleButton />
            <LanguageButton />
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t("openDrawer")}
              onClick={apriMenu(true)}
              sx={{
                paddingLeft: { xs: "6px", sm: "12px" },
                paddingRight: { xs: "6px", sm: "12px" },
                marginLeft: "0px",
              }}
            >
              <MenuIcon color="primary" />
            </IconButton>
          </Grid>

          <Drawer
            sx={{
              height: "3vh",
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 320,
              },
            }}
            open={open}
            onChange={chiudiMenu}
            onClose={() => setOpen(false)}
            anchor="right"
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t("openDrawer")}
              onClick={chiudiMenu}
            >
              <CloseIcon color="primary" />
            </IconButton>

            <Divider />

            <List sx={{ py: 1.5 }}>
              {[
                {
                  key: "operativita",
                  items: [
                    ...(isConferenceTenant || isSpaTenant ? [] : [{ to: "/home", primary: t("home") }]),
                    ...(isSpaTenant
                      ? [
                          { to: "/spa/dashboard", primary: t("spa.menu.dashboard", { defaultValue: "SPA dashboard" }) },
                          { to: "/spa/operations", primary: t("spa.menu.operations", { defaultValue: "Check-in & Treatments" }) },
                          { to: "/mappa_tavoli", primary: getTenantTranslation(t, tenantType, "tablesMap") },
                          { to: "/punto_cassa", primary: getTenantTranslation(t, tenantType, "checkoutPage") },
                        ]
                      : [
                          { to: "/mappa_tavoli", primary: getTenantTranslation(t, tenantType, "tablesMap") },
                          { to: "/punto_cassa", primary: getTenantTranslation(t, tenantType, "checkoutPage") },
                        ]),
                  ],
                },
                {
                  key: "prenotazioni",
                  items: [
                    ...(isSpaTenant
                      ? [
                          { to: "/spa/planner", primary: t("spa.planner.menu", { defaultValue: "SPA Planner" }) },
                          { to: "/spa/clients", primary: t("spa.menu.clients", { defaultValue: "SPA Costumers" }) },
                          { to: "/spa/waitlist", primary: t("spa.waitlist.title", { defaultValue: "SPA waitlist" }) },
                        ]
                      : [
                          { to: "/prenotazione", primary: isConferenceTenant ? t("planner") : t("reservation") },
                        ]),
                    ...(isConferenceTenant ? [{ to: "/monitor-eventi", primary: t("monitorEventi.menu", { defaultValue: "Monitor eventi" }) }] : []),
                    ...(isSpaTenant
                      ? []
                      : [
                          { to: "/lista_prenotazioni", primary: t("list_reservation") },
                          ...(isConferenceTenant ? [] : [{ to: "/preventivi", primary: t("preventivi.page") }]),
                          { to: "/comunicazioni", primary: t("comunicazioni.page") },
                          { to: "/clienti", primary: t("clients") },
                          { to: "/storico-ordini-cliente", primary: t("CustomerOrderHistory") },
                        ]),
                  ],
                },
                {
                  key: "catalogo",
                  items: isSpaTenant
                    ? [
                      { to: "/spa/catalog", primary: t("spa.catalog.menu", { defaultValue: "SPA Catalog" }) },
                    ]
                    : [
                      { to: "/lista_prodotti", primary: getTenantTranslation(t, tenantType, "products") },
                      { to: "/varianti", primary: "Varianti" },
                      { to: "/menu", primary: getTenantTranslation(t, tenantType, "menu_config.page") },
                      { to: "/categorie", primary: t("manage_categories.page") },
                      { to: "/allestimenti-sale", primary: isConferenceTenant ? t("pacchetti") : getTenantTranslation(t, tenantType, "allestimenti.pageTitleShort") },
                      ...(normalizedTenantType === "RISTORANTE"
                        ? [{ to: "/menu-prezzo-fisso", primary: t("fixedPriceMenu.title") }]
                        : []),
                      ...(isConferenceTenant ? [{ to: "/allestimenti-congressi", primary: t("allestimentisale") }] : []),
                      ...(isConferenceTenant ? [{ to: "/tipi-utilizzo", primary: t("tipiutilizzo") }] : []),
                      ...(isConferenceTenant ? [{ to: "/reparti", primary: t("reparti", { defaultValue: "Reparti" }) }] : []),
                  ],
                },
                {
                  key: "amministrazione",
                  items: [
                    ...(isSpaTenant
                      ? [
                          { to: "/spa/finance", primary: t("spa.menu.finance", { defaultValue: "SPA finance" }) },
                          { to: "/spa/reports", primary: t("spa.menu.reports", { defaultValue: "SPA reports" }) },
                        ]
                      : []),
                    { to: "/documenti-emessi", primary: t("issuedDocuments.menu") },
                    { to: "/fatturazione", primary: t("billing.page") },
                    { to: "/statistiche", primary: t("stats.page") },
                  ],
                },
                {
                  key: "sistema",
                  items: [
                    ...(isSpaTenant ? [{ to: "/spa/communications", primary: t("spa.communications.pageTitle") }] : []),
                    { to: "/utenti", primary: t("user_management.page") },
                    ...(canSeeLogs ? [{ to: "/logs", primary: t("menuGroups.operationsLog") }] : []),
                    { to: "/impostazioni-sistema", primary: t("systemSettings.page", { defaultValue: t("menuGroups.systemSettings") }) },
                    { to: "/landing_page", primary: t("menuGroups.landingPage") },
                  ],
                },
              ].map(renderMenuGroup)}

              <Box sx={{ mx: 1.5, mt: 1 }}>
                {can('sync:manage') && <SyncButton />}
                <LogoutButton />
              </Box>
            </List>
          </Drawer>
        </Grid>
      </Box>
    </>
  );
}

export default AppSidebar;
